<template>
  <div class="template-page" v-loading="loading">
    <header class="page-heading">
      <h2>报告模板</h2>
      <div class="actions">
        <el-button :icon="Refresh" @click="load" :disabled="busy">刷新</el-button>
        <el-button type="primary" :icon="Plus" :disabled="busy || loading" @click="openCreate">新增模板</el-button>
      </div>
    </header>
    <el-form inline class="filters" @submit.prevent>
      <el-form-item label="模板名称"><el-input v-model="filter.name" clearable /></el-form-item>
      <el-form-item label="分类"><el-select v-model="filter.category" clearable style="width: 140px"><el-option v-for="category in categories" :key="category" :value="category" :label="category" /></el-select></el-form-item>
      <el-form-item label="状态"><el-select v-model="filter.status" clearable style="width: 140px"><el-option :value="1" label="启用" /><el-option :value="0" label="停用" /></el-select></el-form-item>
    </el-form>
    <el-table :data="filtered" row-key="id" border>
      <el-table-column prop="name" label="模板名称" min-width="170" />
      <el-table-column prop="category" label="分类" width="105" />
      <el-table-column label="类型" width="100"><template #default="{ row }">{{ row.templateType === 'FRAGMENT' ? '公共片段' : '报告' }}</template></el-table-column>
      <el-table-column prop="fileName" label="模板标识" min-width="220" />
      <el-table-column label="状态" width="120"><template #default="{ row }"><el-tag :type="row.status !== 1 ? 'info' : row.publishedVersionId ? 'success' : 'warning'">{{ row.status !== 1 ? '已停用' : row.publishedVersionId ? '已发布' : '待发布' }}</el-tag></template></el-table-column>
      <el-table-column label="草稿" width="110"><template #default="{ row }"><el-tag v-if="row.draftVersionId !== row.publishedVersionId" type="warning">未发布</el-tag><span v-else>已同步</span></template></el-table-column>
      <el-table-column prop="updateTime" label="更新时间" min-width="170" />
      <el-table-column label="操作" width="240" fixed="right"><template #default="{ row }">
        <el-button size="small" :icon="Edit" @click="open(row)" :disabled="busy">编辑</el-button>
        <el-button size="small" :icon="Clock" @click="history(row)" :disabled="busy">版本</el-button>
        <el-button size="small" @click="toggle(row)" :disabled="busy">{{ row.status === 1 ? '停用' : '启用' }}</el-button>
      </template></el-table-column>
      <template #empty><el-empty description="暂无报告模板" /></template>
    </el-table>

    <el-dialog v-model="editor.visible" :title="editor.creating ? '新增模板' : editor.detail?.template.name || '编辑模板'" width="96%" top="3vh" :close-on-click-modal="false" :before-close="beforeClose">
      <template v-if="editor.creating || editor.detail">
        <el-form class="template-meta" label-width="80px" @submit.prevent>
          <el-form-item label="模板名称" required>
            <el-input v-model="editor.name" :disabled="busy" maxlength="100" show-word-limit aria-label="模板名称" />
          </el-form-item>
          <template v-if="editor.creating">
            <el-form-item label="模板标识" required>
              <el-input v-model="editor.fileName" :disabled="busy" maxlength="85" placeholder="customReport.ftl" aria-label="模板标识" />
            </el-form-item>
            <el-form-item label="分类" required>
              <el-select v-model="editor.category" filterable allow-create default-first-option :disabled="busy" style="width: 100%" aria-label="模板分类">
                <el-option v-for="category in categories" :key="category" :value="category" :label="category" />
              </el-select>
            </el-form-item>
          </template>
        </el-form>
        <div class="editor-toolbar">
          <div class="version-tags"><el-tag>生效版本 {{ editor.detail?.published?.versionNo || '--' }}</el-tag><el-tag type="warning">草稿版本 {{ editor.detail?.draft?.versionNo || '--' }}</el-tag><el-tag v-if="dirty" type="danger">未保存</el-tag></div>
          <div class="actions">
            <el-radio-group v-model="editor.tab" size="small"><el-radio-button label="template">模板</el-radio-button><el-radio-button label="sample">样例数据</el-radio-button></el-radio-group>
            <el-button :icon="View" :loading="previewing" :disabled="busy || editor.creating" @click="preview">预览</el-button>
          </div>
        </div>
        <div v-if="editor.detail?.template.templateType === 'FRAGMENT'" class="fragment-row">
          <label for="main-template">预览报告</label>
          <el-select id="main-template" v-model="editor.mainTemplate" style="width: 260px" @change="selectMain"><el-option v-for="file in editor.detail.affectedReports" :key="file" :value="file" :label="nameFor(file)" /></el-select>
          <span class="muted">关联 {{ editor.detail.affectedReports.length }} 份报告</span>
        </div>
        <div class="editor-columns">
          <section class="source-pane">
            <textarea v-if="editor.tab === 'template'" v-model="editor.content" :disabled="busy" class="code-editor" spellcheck="false" aria-label="FreeMarker模板内容" />
            <textarea v-else v-model="editor.sampleJson" :disabled="busy" class="code-editor" spellcheck="false" aria-label="脱敏样例JSON" />
          </section>
          <section class="preview-pane" v-loading="previewing">
            <iframe v-if="previewHtml" :srcdoc="previewHtml" sandbox="" referrerpolicy="no-referrer" title="报告预览" />
            <el-empty v-else description="暂无预览" />
          </section>
        </div>
        <el-form class="change-form" label-width="80px"><el-form-item label="修改说明" required><el-input v-model="editor.changeNote" maxlength="255" show-word-limit /></el-form-item></el-form>
      </template>
      <template #footer><el-button @click="closeEditor" :disabled="busy">关闭</el-button><el-button :icon="DocumentChecked" :loading="busy" @click="save">保存草稿</el-button><el-button type="primary" :icon="Upload" :disabled="dirty || !editor.detail || editor.detail.draft?.id === editor.detail.published?.id" :loading="busy" @click="publish">发布</el-button></template>
    </el-dialog>

    <el-dialog v-model="versions.visible" :title="`${versions.row?.name || ''} · 版本记录`" width="min(1000px, 96vw)" :close-on-click-modal="false">
      <el-table :data="versions.items" border v-loading="versions.loading">
        <el-table-column prop="versionNo" label="版本" width="75" />
        <el-table-column prop="changeNote" label="修改说明" min-width="170" />
        <el-table-column label="发布状态" width="110"><template #default="{ row }">{{ row.id === versions.row?.publishedVersionId ? '当前发布' : row.publishedAt ? '历史发布' : '草稿' }}</template></el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="165" />
        <el-table-column prop="publishedAt" label="发布时间" min-width="165" />
        <el-table-column label="操作" width="170"><template #default="{ row }"><el-button size="small" @click="viewVersion(row)">查看</el-button><el-button size="small" :disabled="!row.publishedAt || row.id === versions.row?.publishedVersionId || busy" @click="rollback(row)">回退</el-button></template></el-table-column>
      </el-table>
    </el-dialog>
    <el-dialog v-model="source.visible" :title="`版本 ${source.versionNo} · 模板内容`" width="90%"><textarea class="code-editor history-source" readonly :value="source.content" aria-label="历史模板内容" /></el-dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { Clock, DocumentChecked, Edit, Plus, Refresh, Upload, View } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { listReportTemplates, getReportTemplate, createReportTemplate, saveReportTemplateDraft, previewReportTemplate,
  publishReportTemplate, changeReportTemplateStatus, rollbackReportTemplate, listReportTemplateVersions, getReportTemplateVersion } from "@/api/reportTemplate";

const rows = ref([]), loading = ref(false), busy = ref(false), previewing = ref(false);
const filter = reactive({ name: "", category: "", status: "" });
const editor = reactive({ visible: false, creating: false, detail: null, name: "", savedName: "", fileName: "", category: "", content: "", sampleJson: "", changeNote: "", tab: "template", mainTemplate: "", savedContent: "", savedSample: "" });
const versions = reactive({ visible: false, loading: false, row: null, items: [] });
const source = reactive({ visible: false, content: "", versionNo: null });
const rawPreview = ref("");
const previewHtml = computed(() => rawPreview.value ? '<meta http-equiv="Content-Security-Policy" content="script-src \'none\'; connect-src \'none\'; form-action \'none\'; frame-src \'none\'; object-src \'none\'; base-uri \'none\'">' + rawPreview.value : "");
const dirty = computed(() => editor.name !== editor.savedName || editor.content !== editor.savedContent || editor.sampleJson !== editor.savedSample || !!editor.changeNote.trim() || (editor.creating && !!(editor.fileName || editor.category)));
const categories = computed(() => [...new Set(rows.value.map(row => row.category))]);
const filtered = computed(() => rows.value.filter(row => row.name.includes(filter.name.trim()) && (!filter.category || row.category === filter.category) && (filter.status === "" || filter.status == null || row.status === filter.status)));
const nameFor = file => rows.value.find(row => row.fileName === file)?.name || file;
const error = e => ElMessage.error(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "操作失败，请重试");
async function confirm(message) { try { await ElMessageBox.confirm(message, "请确认", { type: "warning", confirmButtonText: "确认", cancelButtonText: "取消" }); return true; } catch { return false; } }
async function load() { loading.value = true; try { rows.value = await listReportTemplates(); } catch (e) { error(e); } finally { loading.value = false; } }
function openCreate() {
  Object.assign(editor, { visible: true, creating: true, detail: null, name: "", savedName: "", fileName: "", category: "", content: "", savedContent: "", sampleJson: "{}", savedSample: "{}", changeNote: "", tab: "template", mainTemplate: "" });
  rawPreview.value = "";
}
function setDetail(detail) {
  editor.creating = false;
  editor.detail = detail; editor.content = detail.draft?.content || ""; editor.sampleJson = detail.draft?.sampleJson || "{}";
  editor.name = detail.template.name; editor.savedName = editor.name;
  editor.savedContent = editor.content; editor.savedSample = editor.sampleJson; editor.changeNote = "";
}
async function open(row) {
  busy.value = true;
  try { setDetail(await getReportTemplate(row.id)); rawPreview.value = ""; editor.tab = "template"; editor.mainTemplate = editor.detail.affectedReports[0] || ""; editor.visible = true; if (row.templateType === "FRAGMENT") await selectMain(); }
  catch (e) { error(e); } finally { busy.value = false; }
}
async function selectMain() {
  rawPreview.value = "";
  const main = rows.value.find(row => row.fileName === editor.mainTemplate);
  if (!main) return;
  try { const detail = await getReportTemplate(main.id); editor.sampleJson = detail.published?.sampleJson || detail.draft?.sampleJson || "{}"; editor.savedSample = editor.sampleJson; } catch (e) { error(e); }
}
function validate() {
  if (!editor.content.trim()) throw new Error("模板内容不能为空");
  const encoder = new TextEncoder();
  if (encoder.encode(editor.content).length > 512 * 1024) throw new Error("模板内容不能超过512KB");
  if (encoder.encode(editor.sampleJson).length > 256 * 1024) throw new Error("样例数据不能超过256KB");
  let sample; try { sample = JSON.parse(editor.sampleJson); } catch { throw new Error("样例数据不是有效JSON"); }
  if (sample == null || Array.isArray(sample) || typeof sample !== "object") throw new Error("样例数据必须是JSON对象");
}
async function preview() {
  try { validate(); } catch (e) { return error(e); }
  previewing.value = true; rawPreview.value = "";
  try { rawPreview.value = await previewReportTemplate(editor.detail.template.id, { content: editor.content, sampleJson: editor.sampleJson, mainTemplate: editor.mainTemplate }); }
  catch (e) { error(e); } finally { previewing.value = false; }
}
async function save() {
  try {
    if (!editor.name.trim()) throw new Error("模板名称不能为空");
    if (editor.name.trim().length > 100) throw new Error("模板名称不能超过100字");
    if (editor.creating) {
      const fileName = editor.fileName.trim();
      if (!/^[A-Za-z][A-Za-z0-9_-]{0,79}\.(html|ftl)$/.test(fileName)) throw new Error("模板标识须以英文字母开头，主体最多80位，仅含字母、数字、下划线或短横线，并以.html或.ftl结尾");
      if (!editor.category.trim() || editor.category.trim().length > 32) throw new Error("请填写32字以内的分类");
      if (rows.value.some(row => row.fileName.toLowerCase() === fileName.toLowerCase() || row.code?.toLowerCase() === fileName.slice(0, fileName.lastIndexOf('.')).toLowerCase())) throw new Error("模板标识已存在，请使用其他标识或编辑已有模板");
    }
    validate(); if (!editor.changeNote.trim()) throw new Error("请填写修改说明");
  } catch (e) { return error(e); }
  busy.value = true;
  try {
    const creating = editor.creating;
    const body = { name: editor.name.trim(), content: editor.content, sampleJson: editor.sampleJson, changeNote: editor.changeNote.trim() };
    const detail = creating
      ? await createReportTemplate({ ...body, fileName: editor.fileName.trim(), category: editor.category.trim() })
      : await saveReportTemplateDraft(editor.detail.template.id, { ...body, revision: editor.detail.template.revision });
    setDetail(detail); editor.mainTemplate = detail.affectedReports[0] || "";
    ElMessage.success(creating ? "模板已新增，尚未发布" : "草稿已保存，尚未发布"); await load();
  }
  catch (e) { error(e); } finally { busy.value = false; }
}
async function publish() {
  if (dirty.value) return ElMessage.warning("请先保存草稿");
  const fragment = editor.detail.template.templateType === "FRAGMENT" ? `将影响 ${editor.detail.affectedReports.map(nameFor).join("、")}。` : "";
  if (!await confirm(`${fragment}发布后用于新生成报告，已有报告不变。确认发布？`)) return;
  busy.value = true;
  try { setDetail(await publishReportTemplate(editor.detail.template.id, { revision: editor.detail.template.revision, versionId: editor.detail.draft.id })); ElMessage.success("模板已发布"); await load(); }
  catch (e) { error(e); } finally { busy.value = false; }
}
async function toggle(row) {
  if (!await confirm(row.status === 1 ? `停用“${row.name}”后${row.templateType === 'FRAGMENT' ? '引用此片段的报告' : '该报告'}将无法生成，不会回退到内置模板，已有报告不变。确认停用？` : `确认启用“${row.name}”的已发布模板？`)) return;
  busy.value = true;
  try { await changeReportTemplateStatus(row.id, { revision: row.revision, status: row.status === 1 ? 0 : 1 }); await load(); ElMessage.success("状态已更新"); }
  catch (e) { error(e); } finally { busy.value = false; }
}
async function history(row) {
  versions.row = row; versions.visible = true; versions.loading = true;
  try { const detail = await getReportTemplate(row.id); versions.row = detail.template; versions.items = await listReportTemplateVersions(row.id); }
  catch (e) { error(e); } finally { versions.loading = false; }
}
async function viewVersion(row) {
  try { const result = await getReportTemplateVersion(versions.row.id, row.id); source.content = result.content; source.versionNo = result.versionNo; source.visible = true; } catch (e) { error(e); }
}
async function rollback(row) {
  if (!await confirm(`回退到版本 ${row.versionNo}，将建立并发布一个新版本，已有报告不变。继续？`)) return;
  busy.value = true;
  try { const detail = await rollbackReportTemplate(versions.row.id, { revision: versions.row.revision, versionId: row.id }); ElMessage.success("已回退并发布"); await load(); await history(detail.template); }
  catch (e) { error(e); } finally { busy.value = false; }
}
async function beforeClose(done) { if (busy.value || previewing.value) return; if (!dirty.value || await confirm("有未保存修改，确认关闭？")) done(); }
function closeEditor() { beforeClose(() => { editor.visible = false; }); }
onMounted(load);
</script>

<style scoped>
.template-page { padding: 16px; background: #fff; min-height: 100%; box-sizing: border-box; }
.page-heading, .editor-toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
.page-heading h2 { margin: 0; font-size: 18px; font-weight: 600; }
.actions, .version-tags, .fragment-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.actions :deep(.el-button + .el-button) { margin-left: 0; }
.template-meta { max-width: 640px; }
.template-meta :deep(.el-form-item) { margin-bottom: 14px; }
.fragment-row { margin: 0 0 12px; }
.muted { color: #737b86; font-size: 12px; }
.editor-columns { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); height: 58vh; min-height: 330px; gap: 12px; }
.source-pane, .preview-pane { min-width: 0; min-height: 0; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.code-editor { display: block; width: 100%; height: 100%; resize: none; box-sizing: border-box; border: 0; outline: none; padding: 12px; font: 13px/1.7 Consolas, monospace; color: #303133; background: #fafbfc; white-space: pre; tab-size: 2; }
.code-editor:focus { outline: 2px solid var(--el-color-primary); outline-offset: -2px; }
.preview-pane iframe { border: 0; width: 100%; height: 100%; background: white; }
.change-form { margin-top: 14px; }
.change-form :deep(.el-form-item) { margin-bottom: 0; }
.history-source { height: 65vh; border: 1px solid #dcdfe6; }
@media (max-width: 760px) { .editor-columns { grid-template-columns: minmax(0, 1fr); grid-template-rows: 260px 400px; height: auto; } .template-page { padding: 12px; } }
</style>
